<template>
    <v-container fluid class="pa-4">
        <v-card class="mb-4">
            <v-card-title class="text-h6">
                <v-icon left color="primary">mdi-chart-donut</v-icon>
                Report HR
                <v-spacer></v-spacer>
                <v-btn icon @click="loadAll" :loading="loading"><v-icon>mdi-refresh</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <v-row align="center" dense>
                    <v-col cols="12" md="3">
                        <gmh-date-picker v-model="from" label="Dal" prepend-icon="mdi-calendar-start" hide-details @change="loadAll" editable :clearable="false"></gmh-date-picker>
                    </v-col>
                    <v-col cols="12" md="3">
                        <gmh-date-picker v-model="to" label="Al" prepend-icon="mdi-calendar-end" hide-details @change="loadAll" editable :clearable="false"></gmh-date-picker>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-row>
            <v-col cols="12" md="4">
                <v-card>
                    <v-card-title class="text-subtitle-1"><v-icon left>mdi-account-group</v-icon>Headcount</v-card-title>
                    <v-card-text>
                        <div class="text-h3 primary--text">{{ headcount.total || 0 }}</div>
                        <div class="text-caption grey--text">Dipendenti totali</div>
                        <v-divider class="my-2"></v-divider>
                        <v-chip v-for="r in (headcount.by_status || [])" :key="r.employment_status" small class="mr-1 mb-1" :color="statusColor(r.employment_status)" text-color="white">
                            {{ statusText(r.employment_status) }}: {{ r.count }}
                        </v-chip>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="4">
                <v-card>
                    <v-card-title class="text-subtitle-1"><v-icon left>mdi-beach</v-icon>Assenteismo</v-card-title>
                    <v-card-text>
                        <div class="text-h3 primary--text">{{ absenceTotalHours }}h</div>
                        <div class="text-caption grey--text">Ore assenza approvate nel periodo</div>
                        <v-divider class="my-2"></v-divider>
                        <div v-for="r in (absenteeism.by_type || [])" :key="r.code" class="d-flex justify-space-between text-body-2">
                            <span>{{ r.name }}</span>
                            <strong>{{ parseFloat(r.hours).toFixed(1) }}h</strong>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="4">
                <v-card>
                    <v-card-title class="text-subtitle-1"><v-icon left>mdi-file-alert</v-icon>Documenti in scadenza</v-card-title>
                    <v-card-text>
                        <div class="text-h3 orange--text">{{ docExpiry.rows ? docExpiry.rows.length : 0 }}</div>
                        <div class="text-caption grey--text">Nei prossimi 60 giorni</div>
                        <v-divider class="my-2"></v-divider>
                        <div v-for="r in (docExpiry.rows || []).slice(0, 5)" :key="r.id" class="text-body-2">
                            <div>{{ r.name }}</div>
                            <div class="text-caption grey--text">{{ r.employee_full_name }} · {{ formatDate(r.expires_at) }}</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title class="text-subtitle-1"><v-icon left>mdi-clock-alert</v-icon>Straordinari</v-card-title>
                    <v-data-table :headers="overtimeHeaders" :items="overtime.rows || []" disable-pagination hide-default-footer dense>
                        <template v-slot:item.minutes_worked="{ item }">{{ formatMinutes(item.minutes_worked) }}</template>
                        <template v-slot:item.minutes_overtime="{ item }"><strong class="orange--text">{{ formatMinutes(item.minutes_overtime) }}</strong></template>
                    </v-data-table>
                </v-card>
            </v-col>

            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title class="text-subtitle-1"><v-icon left>mdi-cash-multiple</v-icon>Costi per commessa</v-card-title>
                    <v-data-table :headers="costsHeaders" :items="costs.rows || []" disable-pagination hide-default-footer dense>
                        <template v-slot:item.hours="{ item }">{{ parseFloat(item.hours || 0).toFixed(2) }}h</template>
                        <template v-slot:item.cost="{ item }"><strong>{{ formatCurrency(item.cost) }}</strong></template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-title class="text-subtitle-1"><v-icon left>mdi-alert-octagram</v-icon>Anomalie cartellino</v-card-title>
                    <v-data-table :headers="anomaliesHeaders" :items="anomalies.rows || []" disable-pagination hide-default-footer dense>
                        <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
                        <template v-slot:item.anomalies="{ item }">
                            <v-chip v-for="a in parseAnomalies(item.anomalies)" :key="a" x-small class="mr-1" :color="isBreakAnomaly(a) ? 'blue-grey' : 'orange'" text-color="white">{{ anomalyLabel(a, item) }}</v-chip>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import { anomalyLabel, isBreakAnomaly } from '@/components/utilities/hrColors'

    export default {
        name: 'hr-reports',

        data() {
            return {
                loading: false,
                from: this.yearStart(),
                to: this.yearEnd(),
                headcount: {},
                absenteeism: {},
                docExpiry: {},
                overtime: {},
                costs: {},
                anomalies: {},
                overtimeHeaders: [
                    { text: 'Dipendente', value: 'employee_full_name' },
                    { text: 'Ore lavorate', value: 'minutes_worked' },
                    { text: 'Straord.', value: 'minutes_overtime' },
                ],
                costsHeaders: [
                    { text: 'Progetto', value: 'project_name' },
                    { text: 'Ore', value: 'hours' },
                    { text: 'Costo', value: 'cost' },
                ],
                anomaliesHeaders: [
                    { text: 'Data', value: 'date' },
                    { text: 'Dipendente', value: 'employee_full_name' },
                    { text: 'Anomalie', value: 'anomalies' },
                ],
            }
        },

        computed: {
            absenceTotalHours() {
                return parseFloat((this.absenteeism.by_type || []).reduce((s, r) => s + parseFloat(r.hours || 0), 0)).toFixed(1)
            },
        },

        methods: {
            yearStart() { return `${new Date().getFullYear()}-01-01` },
            yearEnd() { return `${new Date().getFullYear()}-12-31` },
            loadAll() {
                this.loading = true
                Promise.all([
                    this.axios.get('hrreports/headcount'),
                    this.axios.get(`hrreports/absenteeism?from=${this.from}&to=${this.to}`),
                    this.axios.get(`hrreports/overtime?from=${this.from}&to=${this.to}`),
                    this.axios.get('hrreports/document-expiry?days=60'),
                    this.axios.get(`hrreports/anomalies?from=${this.from}&to=${this.to}`),
                    this.axios.get(`hrreports/costs-per-project?from=${this.from}&to=${this.to}`),
                ]).then(([h, a, o, d, an, c]) => {
                    this.headcount = h.data
                    this.absenteeism = a.data
                    this.overtime = o.data
                    this.docExpiry = d.data
                    this.anomalies = an.data
                    this.costs = c.data
                    this.loading = false
                }, () => { this.loading = false })
            },
            statusText(s) { return { active: 'Attivi', suspended: 'Sospesi', terminated: 'Cessati' }[s] || s },
            statusColor(s) { return { active: 'green', suspended: 'orange', terminated: 'grey' }[s] || 'grey' },
            parseAnomalies(a) { if (Array.isArray(a)) return a; try { return JSON.parse(a) || [] } catch(e) { return [] } },
            anomalyLabel,
            isBreakAnomaly,
            formatMinutes(m) { if (!m) return '0h'; const h = Math.floor(m / 60); const mm = m % 60; return `${h}h ${String(mm).padStart(2, '0')}m` },
            formatDate(d) { return d ? new Date(d).toLocaleDateString('it-IT') : '—' },
            formatCurrency(v) { return v != null ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v) : '—' },
        },

        created() {
            this.$store.commit('endProgress')
            this.loadAll()
        },
    }
</script>
